import React from 'react';
import { css, cx } from '@emotion/css';
import { useNode, useEditor, ROOT_NODE } from '@craftjs/core';

import type { CSSInterpolation, CSSObject } from '@emotion/serialize';
import { EMBED_NODE_NAME, EMBED_NODE_HEIGHT } from '@builder4/shared/utils/constants';
import type { TRBLNumberCOORDS } from '@builder4/shared/types/layout';
import type { NodeCustomProps } from '@builder4/shared/types/craft';
import {
  getVisibilityClasses,
  DEFAULT_VISIBILITY,
  getPositionStyle,
  getPaddingStyle,
  getMarginStyle,
  getBorderRadiusStyle,
  getPositionPropsV2,
  getGlobalStyleColor,
  getMediaQueryHeader,
} from '@builder4/shared/utils/styleHelpers';
import { useRenderContext } from '@builder4/shared/context/RenderContext';
import type { StylesConfig } from '@builder4/shared/types/styles';
import { EmbedPropsDto } from '@builder4/shared/dto/craft/props/EmbedPropsDto';
import { getBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import {
  getAnimationDataAttributes,
  getAnimationInitialClass,
  getHoverStyles,
} from '../../utils/animationHelpers';

export type EmbedProps = EmbedPropsDto;

export const defaultProps: Partial<EmbedProps> = {
  sourceType: 'html',
  url: '',
  htmlCode: '',

  opacity: 1,

  width: '100%',
  height: `${EMBED_NODE_HEIGHT}px`,
  minWidth: 0,
  minHeight: 0,
  aspectRatioLock: false,
  aspectRatio: undefined,

  padding: [0, 0, 0, 0],
  margin: [0, 0, 0, 0],
  borderRadius: [0, 0, 0, 0],
  borderWidth: 0,
  borderColor: undefined,
  backgroundColor: undefined,

  position: 'relative',
  top: '0px',
  left: '0px',
  right: undefined,
  bottom: undefined,
  zIndex: 0,
  positionAnchorsState: {
    enableTop: true,
    enableRight: false,
    enableBottom: false,
    enableLeft: true,
    enabled: false,
  },

  fillSpace: 'yes',
  flexBasis: '100%',
  flexGrow: 0,

  visibility: DEFAULT_VISIBILITY,

  tablet: {},
  mobile: {
    width: '100%',
    fillSpace: 'no',
    flexBasis: '100%',
  },
};

function computeEmbedContentStyles(props: Partial<EmbedProps>, activeGlobalStyles: StylesConfig) {
  const { padding, backgroundColor, borderColor, borderWidth, borderRadius } = props;

  const borderRadiusString = borderRadius
    ? getBorderRadiusStyle(borderRadius as TRBLNumberCOORDS).borderRadius
    : undefined;
  const paddingString = padding ? getPaddingStyle(padding as TRBLNumberCOORDS).padding : undefined;
  const borderWidthString = borderWidth ? `${borderWidth}px` : undefined;
  const background = getGlobalStyleColor(backgroundColor, activeGlobalStyles.colors);
  const borderColorString = getGlobalStyleColor(borderColor, activeGlobalStyles.colors);

  const hoverStylesObj = getHoverStyles(
    props.hoverEffect,
    props.hoverTransitionDuration,
    props.hoverIntensity,
  );

  return {
    padding: paddingString,
    background,
    border:
      borderWidth && borderColorString
        ? `${borderWidthString} solid ${borderColorString}`
        : undefined,
    borderRadius: borderRadiusString,
    ...hoverStylesObj,
  };
}

const embedContentStyle = (
  props: Partial<EmbedProps>,
  isServerRender: boolean,
  activeGlobalStyles: StylesConfig,
) => {
  const currentBpStyles = computeEmbedContentStyles(
    {
      ...props,
      padding: props.padding || [0, 0, 0, 0],
      borderRadius: props.borderRadius || [0, 0, 0, 0],
    },
    activeGlobalStyles,
  );
  const finalStyle: CSSInterpolation = {
    ...(currentBpStyles as CSSObject),
    position: 'relative',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    pointerEvents: 'auto',
  };
  const { mobile, tablet } = props;
  if (mobile) {
    const mobileStyles = computeEmbedContentStyles(mobile, activeGlobalStyles);
    finalStyle[getMediaQueryHeader(isServerRender, 'mobile')] = mobileStyles as CSSObject;
  }
  if (tablet) {
    const tabletStyles = computeEmbedContentStyles(tablet, activeGlobalStyles);
    finalStyle[getMediaQueryHeader(isServerRender, 'tablet')] = tabletStyles as CSSObject;
  }

  return css(finalStyle);
};

const computeEmbedNodeStyles = (props: Partial<EmbedProps>) => {
  const { height, width, fillSpace, flexBasis, margin, zIndex } = props;
  const { position, top, left, right, bottom, positionAnchorsState } = props;
  const { aspectRatioLock, aspectRatio } = props;
  const positionStyle = getPositionStyle(position, top, left, right, bottom, positionAnchorsState);
  const marginString = margin ? getMarginStyle(margin).margin : undefined;

  const isAspectRatioLocked = aspectRatioLock && aspectRatio && aspectRatio > 0;
  const aspectRatioValue = isAspectRatioLocked ? `${aspectRatio} / 1` : undefined;

  let flex: string | undefined;
  if (fillSpace) {
    if (fillSpace === 'yes') {
      if (flexBasis) {
        flex = `1 1 ${flexBasis}`;
      }
    } else {
      flex = 'unset';
    }
  }

  return {
    flex,
    padding: marginString,
    ...positionStyle,
    height: isAspectRatioLocked ? 'auto' : height || undefined,
    width: width || undefined,
    zIndex: zIndex || undefined,
    ...(aspectRatioValue ? { aspectRatio: aspectRatioValue } : {}),
  };
};

function getEmbedNodeStylesObject(props: Partial<EmbedProps>) {
  const currentBpStyles = computeEmbedNodeStyles({
    ...props,
    margin: props.margin || [0, 0, 0, 0],
  });
  const finalStyles = {
    ...currentBpStyles,
    display: 'flex',
    mobile: {} as ReturnType<typeof computeEmbedNodeStyles>,
    tablet: {} as ReturnType<typeof computeEmbedNodeStyles>,
  };

  const { mobile, tablet } = props;
  if (mobile && Object.keys(mobile).length) {
    const positionProps = getPositionPropsV2(mobile as Partial<EmbedProps>, props);
    const mobileStyles = computeEmbedNodeStyles({
      ...mobile,
      ...positionProps,
    } as Partial<EmbedProps>);
    finalStyles.mobile = mobileStyles;
  }
  if (tablet && Object.keys(tablet).length) {
    const positionProps = getPositionPropsV2(tablet as Partial<EmbedProps>, props);
    const tabletStyles = computeEmbedNodeStyles({
      ...tablet,
      ...positionProps,
    } as Partial<EmbedProps>);
    finalStyles.tablet = tabletStyles;
  }

  return finalStyles;
}

const embedNodeStyle = (props: Partial<EmbedProps>, isServerRender: boolean) => {
  const { mobile, tablet, ...baseStyles } = getEmbedNodeStylesObject(props);
  const finalStyles: CSSInterpolation = { ...baseStyles };

  if (mobile && Object.keys(mobile).length) {
    finalStyles[getMediaQueryHeader(isServerRender, 'mobile')] = mobile;
  }
  if (tablet && Object.keys(tablet).length) {
    finalStyles[getMediaQueryHeader(isServerRender, 'tablet')] = tablet;
  }

  return css(finalStyles);
};

const embedIframeStyleFromValues = (opacity: number | undefined, isBuilderMode: boolean) => {
  return css({
    opacity: opacity || undefined,
    width: '100%',
    height: '100%',
    border: 'none',
    pointerEvents: isBuilderMode ? 'none' : 'auto',
  });
};

const Embed = (props: Partial<EmbedProps> & { children?: React.ReactNode }) => {
  const mergedProps = {
    ...defaultProps,
    ...props,
  };

  const { isEditable, environment, styles: activeGlobalStyles } = useRenderContext();

  const {
    id,
    connectors: { connect, drag },
  } = useNode((node) => ({
    id: node.id,
  }));

  const { breakpoint } = useEditor((_, query) => {
    const rootNode = query.node(ROOT_NODE).get();
    return {
      breakpoint: rootNode?.data.props.breakpoint || 'desktop',
    };
  });

  const getProp = <K extends keyof EmbedProps>(key: K) =>
    getBreakpointProp(mergedProps, key, breakpoint);

  const visibility = getProp('visibility');
  const sourceType = getProp('sourceType');
  const url = getProp('url');
  const htmlCode = getProp('htmlCode');
  const opacity = getProp('opacity');

  const { children } = mergedProps;

  const visibilityClasses = getVisibilityClasses(visibility || DEFAULT_VISIBILITY);
  const isServerRender = environment === 'server';
  const renderNodeWrapper = isServerRender || environment === 'inspiration-preview';
  const isBuilderMode = environment === 'builder';

  // Determine if embed has content
  const hasEmbed =
    (sourceType === 'url' && Boolean(url?.trim())) ||
    (sourceType === 'html' && Boolean(htmlCode?.trim()));

  const animationClass = getAnimationInitialClass(mergedProps.animationType, environment);
  const animationAttrs = getAnimationDataAttributes(mergedProps);

  const embedElement = React.createElement(
    'div',
    {
      ref: (ref: HTMLElement | null) => {
        if (ref) connect(drag(ref));
      },
      className: cx(
        'embed-content',
        isEditable && 'node-dashed-indicator boundary-indicator styled-node',
        animationClass,
        embedContentStyle(mergedProps, isServerRender, activeGlobalStyles),
      ),
      ...animationAttrs,
      ...(isBuilderMode ? { 'data-animation-preview': id } : {}),
    },
    <>
      {!hasEmbed && children}
      {hasEmbed && (
        <>
          {sourceType === 'url' && url && (
            <iframe
              src={url}
              className={embedIframeStyleFromValues(opacity, isBuilderMode)}
              title="Embedded content"
              allowFullScreen
            />
          )}
          {sourceType === 'html' && htmlCode && (
            <iframe
              srcDoc={htmlCode}
              className={embedIframeStyleFromValues(opacity, isBuilderMode)}
              title="Embedded HTML content"
            />
          )}
        </>
      )}
    </>,
  );

  // Wrap with embed-node div for server and inspiration-preview rendering
  // (in the editor, the Resizer component provides this wrapper instead)
  if (renderNodeWrapper) {
    if (!hasEmbed) return null;

    return (
      <div
        className={cx(
          'embed-node',
          visibilityClasses,
          animationClass,
          embedNodeStyle(mergedProps, isServerRender),
        )}
        {...animationAttrs}
      >
        {embedElement}
      </div>
    );
  }

  return embedElement;
};

Embed.craft = {
  displayName: EMBED_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: EMBED_NODE_NAME,
    isLocked: false,
  } as NodeCustomProps,
};

export { embedNodeStyle, getEmbedNodeStylesObject };
export default Embed;
